/**
 * Test Drive -- a flat list of every pre-canned sample case. Each row shows
 * the case title, a small module pill, a one-line description, and a single
 * Run button.
 *
 * Routing:
 *   - For `denial-resolve` samples, Run posts the fixture payload to
 *     POST /api/denials/from-fixture, then navigates to /denials/:caseId
 *     with autostart so the standard run flow handles "show results vs.
 *     execute" exactly like a real case.
 *   - Every other module keeps the existing ?sample=<id> deep link so the
 *     module page can auto-load its fixture as before.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { getWorkflowModule, Icon } from '../components/workflow';
import { TEST_CASES } from '../fixtures/testCases';
import { dmClient } from '../services/dmClient';

export default function TestDrivePage() {
  usePageTitle('Test Drive');
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Test Drive · CerebralMachines';
  }, []);

  const runDenialSample = useCallback(async (c) => {
    setBusyId(c.id);
    setError('');
    try {
      const resp = await dmClient.post('/denials/from-fixture', {
        fixtureId: c.id,
        fixture: c.payload,
      });
      const caseId = resp?.data?.caseId;
      if (!caseId) throw new Error('No caseId returned');
      navigate(`/denials/${caseId}`, { state: { autostart: true } });
    } catch (e) {
      const msg = e?.response?.data?.message
        || e?.message
        || 'Failed to start test drive';
      setError(msg);
      setBusyId(null);
    }
  }, [navigate]);

  return (
    <div className="dm-page">
      <header className="dm-page__header">
        <h1 className="dm-page__title">Test Drive</h1>
        <p className="dm-page__lede">
          Pick any case below and click Run. The matching module loads the
          data for you, runs the workflow, and you can review the results,
          download them, and ask follow-up questions in the case chat.
        </p>
      </header>

      {error && <div className="dm-banner dm-banner--error">{error}</div>}

      <ul className="td-list">
        {TEST_CASES.map((c) => {
          const mod = getWorkflowModule(c.moduleId);
          if (!mod) return null;
          const isDenial = c.moduleId === 'denial-resolve';
          const isBusy = busyId === c.id;
          return (
            <li className="td-row" key={c.id}>
              <div className="td-row__main">
                <div className="td-row__head">
                  <span className="td-row__module">{mod.name}</span>
                  <h3 className="td-row__title">{c.title}</h3>
                </div>
                {c.desc && <p className="td-row__desc">{c.desc}</p>}
              </div>
              {isDenial ? (
                <button
                  type="button"
                  className="dm-btn dm-btn--primary td-row__run"
                  onClick={() => runDenialSample(c)}
                  disabled={isBusy || (busyId && busyId !== c.id)}
                >
                  {isBusy ? 'Starting…' : <>Run <Icon.arrow /></>}
                </button>
              ) : (
                <Link
                  to={`${mod.to}?sample=${encodeURIComponent(c.id)}`}
                  className="dm-btn dm-btn--primary td-row__run"
                >
                  Run <Icon.arrow />
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
